<template>
    <div class="create-segment">
        <div class="create-segment__info">
            <div class="create-segment__info-title">
                {{ info[activeInfo].title }}
            </div>
            <div class="create-segment__info-subtitle">
                {{ info[activeInfo].subtitle }}
            </div>
        </div>
        <div class="create-segment__action">
            <pendo-button
                v-if="!segment"
                theme="app"
                type="secondary"
                class="create-segment__action-button"
                size="small"
                @click="onSegmentCreate">
                {{ $t('askPendo.nextActions.Create_segment') }}
            </pendo-button>
            <pendo-router-link
                v-else-if="segment && !canUpdateSegment"
                :to="{ name: 'Segments', query: { segment: segment.id } }"
                target="_blank">
                <pendo-button
                    theme="app"
                    type="secondary"
                    class="create-segment__action-button"
                    size="small">
                    {{ $t('askPendo.nextActions.Open_segment') }}
                </pendo-button>
            </pendo-router-link>
            <pendo-button
                v-else-if="segment && canUpdateSegment"
                theme="app"
                type="secondary"
                class="create-segment__action-button"
                size="small"
                :loading="isSaving"
                @click="updateSegment">
                {{ !isSaving ? $t('Update Segment') : $t('Saving') }}
            </pendo-button>
            <edit-segment-overlay
                location="customerFinder"
                :visible="segmentOverlayVisible"
                @close="onSegmentClose"
                @save="onSegmentSaved" />
        </div>
    </div>
</template>

<script>
import { PendoButton } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import EditSegmentOverlay from '@/segments/components/segment-editor-vue/edit-segment-overlay.vue';
import get from 'lodash/get';
import { mapMutations, mapGetters, mapActions } from 'vuex';
import {
    getCustomerIds,
    buildFiltersFromCustomerIds,
    buildSegment,
} from '@/ask-pendo/next-actions/next-actions-utils.js';
import { compileSegmentPipeline } from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    components: { PendoButton, PendoRouterLink, EditSegmentOverlay },
    props: {
        conversationId: {
            type: String,
            required: true,
        },
        customers: {
            type: Object,
            required: true,
        },
    },
    data () {
        const info = Object.freeze({
            create: {
                title: this.$t('askPendo.nextActions.Create_segment'),
                subtitle: this.$t('askPendo.nextActions.Create_segment_subtitle'),
            },
            created: {
                title: this.$t('askPendo.nextActions.Created_segment'),
                subtitle: this.$t('askPendo.nextActions.Created_segment_subtitle'),
            },
            update: {
                title: this.$t('askPendo.nextActions.Update_segment'),
                subtitle: this.$t('askPendo.nextActions.Update_segment_subtitle'),
            },
        });

        return {
            info,
            segment: null,
            segmentOverlayVisible: false,
            canUpdateSegment: false,
            isSaving: false,
        };
    },
    computed: {
        ...mapGetters('askPendo', ['conversationById']),
        conversation () {
            return this.conversationById(this.conversationId);
        },
        customerList () {
            return getCustomerIds(this.customers);
        },
        activeInfo () {
            if (this.segment !== null && !this.canUpdateSegment) return 'created';
            if (this.segment !== null && this.canUpdateSegment) return 'update';

            return 'create';
        },
    },
    watch: {
        customerList: {
            handler (newList, oldList) {
                if (this.segment !== null && JSON.stringify(newList) !== JSON.stringify(oldList)) {
                    this.canUpdateSegment = true;
                }
            },
        },
    },
    async created () {
        const segmentId = get(this.conversation, 'state_details.cf_segmentId', null);

        if (segmentId) {
            this.segment = await this.loadOne({ id: segmentId });
        }
    },
    methods: {
        ...mapMutations('askPendo', ['addStateToConversation']),
        ...mapActions('segments', ['loadOne', 'simpleSave', 'createSegmentWithDefinition']),
        async onSegmentSaved (segment) {
            this.addStateToConversation({
                conversationId: this.conversationId,
                fieldName: 'segmentId',
                fieldData: segment.id,
            });
            this.segment = segment;
            this.segmentOverlayVisible = false;
        },
        onSegmentClose () {
            this.segmentOverlayVisible = false;
        },
        onSegmentCreate () {
            const productArea = get(this.conversation, 'state_details.cf_productArea', '');
            const draftSegment = buildSegment(this.customers, productArea);
            this.createSegmentWithDefinition(draftSegment);

            this.segmentOverlayVisible = true;
        },
        async updateSegment () {
            this.isSaving = true;

            const filters = buildFiltersFromCustomerIds(this.customerList);
            this.segment.definition = { filters };
            const pipelineProps = await compileSegmentPipeline(this.segment);
            this.segment.pipeline = pipelineProps?.pipeline;
            this.segment = await this.simpleSave({ segment: this.segment });

            this.isSaving = false;
            this.canUpdateSegment = false;
        },
    },
};
</script>

<style scoped lang="less">
.create-segment {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__info-title {
        color: var(--palette-gray-100);
        font-weight: 600;
        font-size: 13px;
        line-height: 18px;
    }

    &__info-subtitle {
        color: var(--palette-gray-70);
        font-size: 12px;
        line-height: 18px;
    }
}
</style>
